import React, { useState, forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Grid,
  TextField,
  Box,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import validationRules from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  inviteNewRegistrations,
  validateImportedEmails,
} from '../../services/subscribers.service';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const ImportSubscriberEmails = ({
  title,
  onCancel,
  snackbarMessageCallback,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      subscribers: '',
    },
  });
  const [loading, setLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [subscriberEmails, setSubscriberEmails] = useState({
    newAdded: [],
    notRegisterd: [],
    invalid: [],
  });
  const [inviting, setInviting] = useState(false);
  const sentInvitationRef = useRef(false);

  const handleInvite = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    setInviting(true);

    inviteNewRegistrations({
      inviteSubscribers: subscriberEmails.notRegisterd,
    })
      .then((res) => {
        if (res.data?.success) {
          sentInvitationRef.current = true;
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: 'Invitation sent successfully.',
          });
        }
        setInviting(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setInviting(false);
      });
  };

  const validateEmails = (payload) => {
    validateImportedEmails(payload)
      .then((res) => {
        setSubscriberEmails({
          newAdded: res.data?.newAddedEmails,
          notRegisterd: res.data?.notRegisterdEmails,
          invalid: res.data?.invalidEmails,
        });
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = async (data) => {
    setLoading(true);
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const subscribers = data.subscribers
      .trim()
      .split('\n')
      .map((email) => email.trim());

    validateEmails({ subscribers });
  };

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth={
        subscriberEmails.newAdded?.length > 0 ||
        subscriberEmails.notRegisterd?.length > 0 ||
        subscriberEmails.invalid?.length > 0
          ? 'lg'
          : 'sm'
      }
    >
      <DialogTitle id="view-dialog-title">
        {title}
        {(subscriberEmails.newAdded?.length > 0 ||
          subscriberEmails.notRegisterd?.length > 0 ||
          subscriberEmails.invalid?.length > 0) &&
          ' - Summary'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
        disabled={inviting}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        {loading && <Loading />}

        {!loading && (
          <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={2}>
              {!subscriberEmails.newAdded?.length &&
                !subscriberEmails.notRegisterd?.length &&
                !subscriberEmails.invalid?.length && (
                  <>
                    <Grid item xs={12} sm={12} md={12}>
                      <Controller
                        name="subscribers"
                        control={control}
                        rules={validationRules.REQUIRED}
                        render={({ field: { onChange, value } }) => (
                          <TextField
                            label="Emails"
                            type="text"
                            multiline
                            minRows={5}
                            maxRows={15}
                            onChange={onChange}
                            value={value}
                            error={!!errors.subscribers}
                            helperText={errors?.subscribers?.message || null}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>

                    <Grid
                      item
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: 2,
                      }}
                    >
                      <Box sx={{ fontWeight: 'bold' }}>Note:&nbsp;</Box>
                      You can add multiple email at a time. But enter one email
                      per line.
                    </Grid>
                  </>
                )}

              {(subscriberEmails.newAdded?.length > 0 ||
                subscriberEmails.notRegisterd?.length > 0 ||
                subscriberEmails.invalid?.length > 0) && (
                <>
                  <Grid item xs={12} sm={4} md={4}>
                    <Box marginY={1}>
                      Existing subscribers:{' '}
                      {subscriberEmails.newAdded?.length || 0}
                      <CopyToClipboard
                        text={subscriberEmails.newAdded?.join('\n')}
                      >
                        <IconButton
                          aria-label="close"
                          onClick={() =>
                            snackbarMessageCallback(
                              'success',
                              'Text copied to clipboard.'
                            )
                          }
                          disabled={!subscriberEmails.newAdded?.length}
                          size="small"
                          sx={{ color: (theme) => theme.palette.grey[500] }}
                        >
                          <Iconify icon="solar:copy-bold" />
                        </IconButton>
                      </CopyToClipboard>
                    </Box>

                    <Grid item xs={12} sm={12} md={12}>
                      <TextField
                        label="Existing subscribers"
                        type="text"
                        multiline
                        minRows={5}
                        maxRows={25}
                        value={subscriberEmails.newAdded?.join('\n')}
                        disabled
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={4} md={4}>
                    <Box
                      marginY={1}
                      display="flex"
                      justifyContent="space-between"
                    >
                      <Box>
                        Emails not registered:{' '}
                        {subscriberEmails.notRegisterd?.length || 0}
                        <CopyToClipboard
                          text={subscriberEmails.notRegisterd?.join('\n')}
                        >
                          <IconButton
                            aria-label="close"
                            onClick={() =>
                              snackbarMessageCallback(
                                'success',
                                'Text copied to clipboard.'
                              )
                            }
                            disabled={!subscriberEmails.notRegisterd?.length}
                            size="small"
                            sx={{ color: (theme) => theme.palette.grey[500] }}
                          >
                            <Iconify icon="solar:copy-bold" />
                          </IconButton>
                        </CopyToClipboard>
                      </Box>

                      <Button
                        type="button"
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={() => handleInvite()}
                        disabled={
                          inviting ||
                          sentInvitationRef.current ||
                          !subscriberEmails.notRegisterd?.length
                        }
                      >
                        {inviting && (
                          <CircularProgress
                            disableShrink
                            size="18px"
                            sx={{ marginRight: 1, color: '#919eabcc' }}
                          />
                        )}
                        {sentInvitationRef.current && 'Invited'}
                        {!sentInvitationRef.current &&
                          (inviting ? 'Inviting...' : 'Invite')}
                      </Button>
                    </Box>

                    <Grid item xs={12} sm={12} md={12}>
                      <TextField
                        label="Emails not registered"
                        type="text"
                        multiline
                        minRows={5}
                        maxRows={25}
                        value={subscriberEmails.notRegisterd?.join('\n')}
                        disabled
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={4} md={4}>
                    <Box marginY={1}>
                      Invalid Emails: {subscriberEmails.invalid?.length || 0}
                      <CopyToClipboard
                        text={subscriberEmails.invalid?.join('\n')}
                      >
                        <IconButton
                          aria-label="close"
                          onClick={() =>
                            snackbarMessageCallback(
                              'success',
                              'Text copied to clipboard.'
                            )
                          }
                          disabled={!subscriberEmails.invalid?.length}
                          size="small"
                          sx={{ color: (theme) => theme.palette.grey[500] }}
                        >
                          <Iconify icon="solar:copy-bold" />
                        </IconButton>
                      </CopyToClipboard>
                    </Box>

                    <Grid item xs={12} sm={12} md={12}>
                      <TextField
                        label="Invalid Emails"
                        type="text"
                        multiline
                        minRows={5}
                        maxRows={25}
                        value={subscriberEmails.invalid?.join('\n')}
                        disabled
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={12} md={12}>
                    <span style={{ fontWeight: 'bold' }}>Note:&nbsp;</span>
                    Invitation emails will be sent out in a few minutes
                  </Grid>
                </>
              )}
            </Grid>
          </form>
        )}
      </DialogContent>

      {!subscriberEmails.newAdded?.length &&
        !subscriberEmails.notRegisterd?.length &&
        !subscriberEmails.invalid?.length && (
          <DialogActions sx={{ justifyContent: 'left', marginLeft: 2 }}>
            <Button
              type="submit"
              form="add-edit-form"
              color="primary"
              variant="contained"
              disabled={loading || formSubmitted}
            >
              Save
            </Button>
            <Button
              color="secondary"
              variant="contained"
              onClick={onCancel}
              disabled={formSubmitted}
            >
              Cancel
            </Button>
          </DialogActions>
        )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

ImportSubscriberEmails.propTypes = {
  title: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  snackbarMessageCallback: PropTypes.func.isRequired,
};

export default ImportSubscriberEmails;
